// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Raw Data Area
export class AreaRawData {
    // --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(dataCore) {
        this.dataCore = dataCore;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Fetch raw data
    /** Fetches raw data necessary for construction of components */
    getRawComponentModelData(designSystemId, versionId, filter) {
        return this.dataCore.read.getRemoteComponents(designSystemId, versionId, filter);
    }
    /** Fetches raw data necessary for component groups */
    getRawComponentGroupModelData(designSystemId, versionId) {
        return this.dataCore.read.getRemoteComponentGroups(designSystemId, versionId);
    }
    /** Fetches raw data necessary for assets */
    getRawAssetModelData(designSystemId, versionId, filter) {
        return this.dataCore.read.getRemoteAssets(designSystemId, versionId, filter);
    }
    /** Fetches raw data necessary for asset groups */
    getRawAssetGroupModelData(designSystemId, versionId, filter) {
        return this.dataCore.read.getRemoteAssetGroups(designSystemId, versionId, filter);
    }
    /** Fetches raw data necessary for tokens */
    getRawTokenModelData(designSystemId, versionId, filter) {
        return this.dataCore.read.getRemoteTokens(designSystemId, versionId, filter);
    }
    /** Fetches raw data necessary for token groups */
    getRawTokenGroupModelData(designSystemId, versionId, filter) {
        return this.dataCore.read.getRemoteTokenGroups(designSystemId, versionId, filter);
    }
    /** Fetches raw data necessary for elements */
    async getRawElementModelData(designSystemId, versionId) {
        const result = await Promise.all([
            this.dataCore.read.getRemoteElementPropertyValues(designSystemId, versionId),
            this.dataCore.read.getRemoteElementDataViewDefinitions(designSystemId, versionId),
            this.dataCore.read.getRemoteElementPropertyDefinitions(designSystemId, versionId),
        ]);
        return {
            propertyValues: result[0],
            propertyViews: result[1],
            propertyDefinitions: result[2],
        };
    }
    /** Fetches raw source data */
    getRawSourceModelData(designSystemId, filter) {
        return this.dataCore.read.getRemoteSources(designSystemId, filter);
    }
    /** Fetches raw font resources data */
    getRawFontResourcesModelData(designSystemId, versionId) {
        return this.dataCore.read.getRemoteFontResources(designSystemId, versionId);
    }
    /** Fetches raw membership */
    getRawMembershipModelData(userId) {
        return this.dataCore.read.getRemoteMemberships(userId);
    }
    /** Fetches raw design systems */
    getRawDesignSystemModelData(workspaceId) {
        return this.dataCore.read.getRemoteDesignSystems(workspaceId);
    }
    /** Fetches raw versions */
    getRawVersionModelData(designSystemId) {
        return this.dataCore.read.getVersions(designSystemId);
    }
    /** Fetches raw brands */
    getRawBrandModelData(designSystemId, versionId) {
        return this.dataCore.read.getBrands(designSystemId, versionId);
    }
    /** Fetches raw current user */
    getRawCurrentUserModelData() {
        return this.dataCore.read.getRemoteCurrentUser();
    }
    /** Fetches raw memberships in a specific workspace */
    getRawWorkspaceMemberships(workspaceId) {
        return this.dataCore.read.getRemoteUserMemberships(workspaceId);
    }
    /** Fetches raw invitations that are part of the workspace */
    getRawWorkspaceInvitations(workspaceId) {
        return this.dataCore.read.getRemoteWorkspaceInvitations(workspaceId);
    }
    /** Fetches raw memberships of a specific user */
    getRawUserMemberships(userId) {
        return this.dataCore.read.getRemoteMemberships(userId);
    }
    /** Fetches raw design components */
    getRawDesignComponentModelData(designSystemId, versionId, filter) {
        return this.dataCore.read.getRemoteDesignComponents(designSystemId, versionId, filter);
    }
    /** Fetches raw design component groups */
    getRawDesignComponentGroupModelData(designSystemId, versionId, filter) {
        return this.dataCore.read.getRemoteDesignComponentGroups(designSystemId, versionId, filter);
    }
    getRawStorybookEntries(designSystemId, versionId, filter) {
        return this.dataCore.read.getStorybookEntries(designSystemId, versionId, filter);
    }
    getRawDocumentationStructure(designSystemId, versionId) {
        return this.dataCore.read.getRemoteRealDocumentationStructure(designSystemId, versionId);
    }
    getRawDocumentationBuilds(workspaceId, versionId, environment, status, createdByUserId, limit = 1) {
        return this.dataCore.read.getRemoteBuilds(workspaceId, versionId, environment, limit, 0, undefined, undefined, ["documentation"], createdByUserId, status);
    }
    async getRawForgeProjects(workspaceId) {
        return this.dataCore.read.getRemoteForgeProjects(workspaceId);
    }
    async getRawForgeProject(workspaceId, projectId) {
        return this.dataCore.read.getRemoteForgeProject(workspaceId, projectId);
    }
    getRawForgeProjectMembers(workspaceId, projectId, isWithImplicitMembers = false) {
        return this.dataCore.read.getRemoteForgeProjectMembers(workspaceId, projectId, isWithImplicitMembers);
    }
    getRawForgeOnboardingContainerUsage(workspaceId) {
        return this.dataCore.read.getRemoteForgeOnboardingContainerUsage(workspaceId);
    }
    async getRawForgeProjectContext(id) {
        return this.dataCore.read.getRemoteForgeProjectContext(id);
    }
    async getRawForgeProjectContexts({ workspaceId, filter, }) {
        return this.dataCore.read.getRemoteForgeProjectContexts({
            workspaceId,
            filter,
        });
    }
    async getRawForgeProjectContextFeedback(contextId) {
        return this.dataCore.read.getRemoteForgeProjectContextFeedback(contextId);
    }
    async getRawForgeWorkspaceFeedback(workspaceId) {
        return this.dataCore.read.getRemoteForgeWorkspaceFeedback(workspaceId);
    }
    async getRawForgeDesignSystemFeedback(designSystemId) {
        return this.dataCore.read.getRemoteForgeDesignSystemFeedback(designSystemId);
    }
    async getRawForgeProjectContextOverride(wsId, projectId) {
        return this.dataCore.read.getRemoteForgeProjectContextOverride(wsId, projectId);
    }
    getRawChatThreads(workspaceId, limit, offset) {
        return this.dataCore.read.getRemoteChatThreads(workspaceId, limit, offset);
    }
    getRawChatThreadMessages(workspaceId, threadId) {
        return this.dataCore.read.getRemoteChatThreadMessages(workspaceId, threadId);
    }
    getRawChatThreadsExport(workspaceId) {
        return this.dataCore.read.getRemoteChatThreadExport(workspaceId);
    }
    getRawPortalSettings(workspaceId) {
        return this.dataCore.read.getRemoteWorkspacePortalSettings(workspaceId);
    }
    getRawWorkspaceSubscription(workspaceId) {
        return this.dataCore.read.getRemoteWorkspaceSubscription(workspaceId);
    }
    getRawWorkspaceProductFeatures(workspaceId) {
        return this.dataCore.read.getRemoteWorkspaceProductFeatures(workspaceId);
    }
    getRawWorkspaceIntegrations(workspaceId) {
        return this.dataCore.read.getRemoteWorkspaceIntegrations(workspaceId);
    }
    getRawDesignSystemContacts(designSystemId) {
        return this.dataCore.read.getRemoteContacts(designSystemId);
    }
    getRawDesignSystemMembersList(designSystemId) {
        return this.dataCore.read.getRemoteDesignSystemMemberList(designSystemId);
    }
    getRawForgeProjectFeatures(workspaceId, projectId) {
        return this.dataCore.read.getRemoteForgeProjectFeatures(workspaceId, projectId);
    }
    getRawThread(threadId) {
        return this.dataCore.read.getRemoteThreads(threadId);
    }
    getRawForgeProjectFeatureIterations(workspaceId, projectId, featureId) {
        return this.dataCore.read.getRemoteForgeProjectFeatureIterations(workspaceId, projectId, featureId);
    }
    getRawForgeProjectFeatureIterationTags(workspaceId, projectId, featureId) {
        return this.dataCore.read.getRemoteForgeProjectFeatureIterationTags(workspaceId, projectId, featureId);
    }
    getRawForgeProjectFeatureSandboxes(workspaceId, projectId, featureId) {
        return this.dataCore.read.getRemoteForgeProjectFeatureSandboxes(workspaceId, projectId, featureId);
    }
    getRawForgeProjectFeatureArtifacts(workspaceId, projectId, featureId, { iterationId, messageId, } = {}) {
        return this.dataCore.read.getRemoteForgeProjectFeatureArtifacts(workspaceId, projectId, featureId, {
            messageId,
            iterationId,
        });
    }
    getRawForgeProjectTrailEvents(projectId) {
        return this.dataCore.read.getRemoteForgeProjectTrailEvents({ projectId });
    }
    getRawPublishedFeature(featureId) {
        return this.dataCore.read.getRemotePublishedFeature(featureId);
    }
    getRawWorkspaceDetail(workspaceId) {
        return this.dataCore.read.getRemoteWorkspaceDetail(workspaceId);
    }
    getRawMCPStream(streamId) {
        return this.dataCore.read.getRemoteMCPStream(streamId);
    }
    getRawUserMCPStream() {
        return this.dataCore.read.getRemoteUserMCPStream();
    }
    getRawSandboxTemplates({ workspaceId, designSystemId, }) {
        return this.dataCore.read.getRemoteSandboxTemplates({
            workspaceId,
            designSystemId,
        });
    }
    getRawWorkspaceSSOSettings(workspaceId) {
        return this.dataCore.read.getRemoteWorkspaceSSOSettings(workspaceId);
    }
}
